import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Search } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useGuilds } from '../hooks/useGuilds';
import { useMyGuilds } from '../hooks/useMyGuilds';
import { GuildCard } from '../components/GuildCard';
import { ArcheHeader } from '../../../components/ArcheHeader';

export function GuildDiscoveryPage() {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [searchInput, setSearchInput] = useState('');

  const { guilds, loading, error, hasMore, loadMore } = useGuilds({ search, sortBy });
  const { guilds: myGuilds, loading: myGuildsLoading } = useMyGuilds();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_guilds" title="Guilds">
        <Link to="/a/arche_guilds/create">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Create Guild
          </Button>
        </Link>
      </ArcheHeader>
      <div className="max-w-6xl mx-auto p-6 space-y-6 w-full">
        <Tabs defaultValue="discover">
          <TabsList>
            <TabsTrigger value="discover">Discover</TabsTrigger>
            <TabsTrigger value="my-guilds">
              My Guilds{myGuilds.length > 0 && ` (${myGuilds.length})`}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="discover" className="space-y-4">
            <div className="flex gap-3">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search guilds..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-9"
                    aria-label="Search guilds"
                  />
                </div>
                <Button type="submit" variant="secondary">
                  Search
                </Button>
              </form>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="popular">Popular</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {loading && guilds.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-36 rounded-lg" />
                ))}
              </div>
            ) : guilds.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No guilds found.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {guilds.map((guild) => (
                    <GuildCard key={guild.guildId} guild={guild} />
                  ))}
                </div>
                {hasMore && (
                  <div className="text-center">
                    <Button variant="ghost" onClick={loadMore}>
                      Load more
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="my-guilds" className="space-y-4">
            {myGuildsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-36 rounded-lg" />
                ))}
              </div>
            ) : myGuilds.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">You haven't joined any guilds yet.</p>
                <Link to="/a/arche_guilds/create">
                  <Button variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Create a Guild
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myGuilds.map((guild) => (
                  <GuildCard key={guild.guildId} guild={guild} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
